function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useCallback, useRef, useEffect, useContext, useState } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";
import { useMediaQuery } from "@churchofjesuschrist/eden-media-query";
import { useClickBoundary } from "@churchofjesuschrist/eden-click-boundary";
import { grey2, grey5, spacing8, spacing48, boxShadowDetached } from "@churchofjesuschrist/eden-style-constants";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { PanelStateContext } from "./panelState";
import PanelHeader from "./PanelHeader";
import PanelFooter from "./PanelFooter";
import PanelMain from "./PanelMain";
import PanelSideContext from "./PanelSideContext";
import hex2rgba from "hex2rgba";
import BodyLock from "@churchofjesuschrist/eden-body-lock";
import { useResize } from "@churchofjesuschrist/eden-resize-observer";
const Overlay = styled.div.withConfig({
  componentId: "sc-10dtmvl-0"
})(["position:absolute;z-index:2;left:0;right:0;top:0;bottom:0;background:", ";visibility:", ";transition:background-color 100ms linear,backdrop-filter 100ms linear;@supports (backdrop-filter:blur(2px)){backdrop-filter:blur(2px);}", ""], hex2rgba(grey2, 0.85), props => props.active ? "visible" : "hidden", _ref => {
  let {
    preferredMode,
    preferredModeBreakWidth
  } = _ref;
  return `
  @media (min-width: ${preferredModeBreakWidth}) {
    ${preferredMode === "persistent" ? `visibility: hidden;` : ``}
    ${preferredMode === "dismissible" ? `visibility: hidden;` : ``}
  }
  `;
});
const animationDuration = "0.4s";
const Body = styled.aside.withConfig({
  componentId: "sc-10dtmvl-1"
})(["", ""], _ref2 => {
  let {
    side,
    open,
    panelWidthBreakWidth,
    panelWidth,
    preferredModeBreakWidth,
    preferredMode
  } = _ref2;
  return css(["grid-area:", ";position:absolute;top:0;", ":0;z-index:1;border-inline-", ":1px solid ", ";transform:", ";transition:", ";background:", ";box-shadow:", ";width:100vw;overflow-x:hidden;@media (min-width:", "){width:", ";}&&{", "}&&&{@media (min-width:", "){", ";", ";}}"], side, side === "start" ? "left" : "right", side === "start" ? "end" : "start", grey5, open ? "translate3d(0,0,0)" : `translate3d(${side === "start" ? "-100%" : "100%"},0,0)`, open ? `transform ${animationDuration}, width ${animationDuration}` : `transform ${animationDuration}, width ${animationDuration}, height 0s ${animationDuration}`, grey2, open ? boxShadowDetached : "none", panelWidthBreakWidth, panelWidth, dir("rtl")`
      transform: ${open ? "translate3d(0,0,0)" : `translate3d(${side === "start" ? "100%" : "-100%"},0,0)`};
      left: ${side === "start" ? "auto" : "0"};
      right: ${side === "start" ? "0" : "auto"};
    `, preferredModeBreakWidth, preferredMode === "persistent" ? `
              position: relative;
              transform: unset;
              box-shadow: unset;
              z-index: unset;
            ` : ``, preferredMode === "dismissible" ? `
              position: relative;
              transform: unset;
              box-shadow: unset;
              z-index: unset;
              ${open ? "" : "width: 0; overflow: hidden;"};
            ` : ``);
});
const InnerWrapper = styled.div.withConfig({
  componentId: "sc-10dtmvl-2"
})(["position:absolute;top:0;bottom:0;display:flex;flex-direction:column;width:100vw;transition:width ", ";overflow-x:hidden;", ";"], animationDuration, _ref3 => {
  let {
    panelWidth,
    panelWidthBreakWidth,
    preferredMode,
    preferredModeBreakWidth
  } = _ref3;
  return `
  ${preferredMode === "persistent" ? `
        @media (min-width: ${preferredModeBreakWidth}) {
          padding-top: ${spacing8};
          padding-bottom: ${spacing48};
        }` : ``};

    @media (min-width: ${panelWidthBreakWidth}) {
      width: ${panelWidth};
    }`;
});

const noop = x => x; // Uses Panel.defaultProps instead of default parameters so that React.cloneElement gets defaults properly


const Panel = _ref4 => {
  let {
    children,
    header,
    backButton,
    footer,
    onClose = noop,
    open,
    preferredMode = "persistent",
    panelWidth = "20rem",
    panelWidthBreakWidth = "25rem",
    preferredModeBreakWidth = "48rem",
    closePanelLabel = "Close",
    ...rest
  } = _ref4;
  const side = useContext(PanelSideContext);
  const onMediaChange = useCallback(() => {}, []);
  const bodyRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [overlayDimension, setOverlayDimension] = useState(0);
  const onResize = useCallback(resizeEntry => {
    setOverlayDimension(Math.round(resizeEntry.contentRect.width + resizeEntry.contentRect.height));
  }, []);
  const [resizeRef] = useResize(onResize);
  const [belowPreferredModeBreakWidth] = useMediaQuery(onMediaChange, `(max-width: ${preferredModeBreakWidth})`);
  const overlayMode = preferredMode === "overlay" || belowPreferredModeBreakWidth;
  const clickBoundaryRef = useClickBoundary({
    //Use setTimeout to push to the end of the call stack so that it fires after all other events.
    onClickOutside: overlayMode && open ? () => setTimeout(onClose, 0) : noop
  });
  useEffect(() => {
    const onScrollHandler = () => {
      setScrollTop(window.scrollY);
    };

    window.addEventListener("scroll", onScrollHandler);
    return () => {
      window.removeEventListener("scroll", onScrollHandler);
    };
  });
  useEffect(() => {
    const panelBody = clickBoundaryRef.current || bodyRef.current;

    if (overlayMode && open) {
      panelBody.style.zIndex = "2";
      panelBody.style.height = `calc(100vh - ${panelBody.getBoundingClientRect().top}px)`;
      panelBody.firstChild.style.cssText = null;
    } else {
      panelBody.style.cssText = null;
      panelBody.style.zIndex = "1"; // sometimes the panels will need to be made shorter than the viewport

      let makeShorterAmount = 0; // get the distance of panels from viewport top

      const offsetTop = panelBody.getBoundingClientRect().top; // all this logic is only needed if the side panels are opened

      if (open) {
        // only switch to fixed when the panelBody would have started going off the top of the screen
        if (offsetTop < 0) {
          panelBody.firstChild.style.position = "fixed";
        } else {
          // if there is a preceding content which pushes the panel down the page, we need to not have the panel be the full height, so subtract the distance from the top of the panel to the top of the viewport
          makeShorterAmount = offsetTop; // remove the position in case someone scrolled down and up to remove the `fixed` which may have been placed

          panelBody.firstChild.style.position = "";
        } // if the bottom of the panel isn't the same as the end of the viewport then there must be content after the panel. Make the panel shorter otherwise they will overlap the trailing content


        const panelBottomFromViewportBottom = window.innerHeight - panelBody.parentNode.getBoundingClientRect().bottom;

        if (panelBottomFromViewportBottom > 0) {
          makeShorterAmount = panelBottomFromViewportBottom + (offsetTop > 0 ? offsetTop : 0);
        }
      } else {
        panelBody.firstChild.style.position = "";
      } // fixed elements break out of parent element, even if they are overflow:hidden, so set the height of the fixed element, not the parent


      panelBody.firstChild.style.height = `calc(100vh - ${makeShorterAmount}px)`;
    }
  }, [clickBoundaryRef, bodyRef, overlayMode, open, scrollTop, overlayDimension]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    ref: resizeRef,
    active: open,
    preferredMode: preferredMode,
    preferredModeBreakWidth: preferredModeBreakWidth
  }), /*#__PURE__*/React.createElement(BodyLock, {
    active: open && overlayMode,
    scrollableRef: clickBoundaryRef
  }), /*#__PURE__*/React.createElement(Body, _extends({
    open: open,
    preferredMode: preferredMode,
    panelWidth: panelWidth,
    panelWidthBreakWidth: panelWidthBreakWidth,
    preferredModeBreakWidth: preferredModeBreakWidth //Do not attach the click event unless the panel is open
    ,
    ref: open ? clickBoundaryRef : bodyRef,
    side: side
  }, rest), /*#__PURE__*/React.createElement(InnerWrapper, {
    preferredMode: preferredMode,
    panelWidthBreakWidth: panelWidthBreakWidth,
    panelWidth: panelWidth,
    preferredModeBreakWidth: preferredModeBreakWidth,
    onMouseDownCapture: event => event.stopPropagation() /// NOT WORKING
    ,
    onTouchStartCapture: event => event.stopPropagation()
  }, /*#__PURE__*/React.createElement(PanelStateContext.Provider, {
    value: {
      onClose,
      closePanelLabel,
      preferredMode,
      preferredModeBreakWidth,
      side
    }
  }, /*#__PURE__*/React.createElement(PanelHeader, {
    backButton: backButton
  }, header), /*#__PURE__*/React.createElement(PanelMain, null, children), footer ? /*#__PURE__*/React.createElement(PanelFooter, null, footer) : null))));
};

Panel.propTypes = {
  /** Content inside the panel */
  children: PropTypes.node.isRequired,

  /** Header content for the panel */
  header: PropTypes.node,

  /** Must be a single `<PanelBackButton>` element */
  backButton: PropTypes.element,

  /** Footer content for the panel */
  footer: PropTypes.node,

  /** Callback to execute when the panel is requested to close */
  onClose: PropTypes.func.isRequired,

  /** Whether the panel should be forced open or closed, uses sensible defaults for the screen size when `undefined` */
  open: PropTypes.bool,

  /** Mode in which the panel should be displayed when the screen size is large enough for it */
  preferredMode: PropTypes.oneOf(["persistent", "overlay", "dismissible"]),

  /** The width of the panel when the viewport is above the `panelWidthBreakWidth` */
  panelWidth: PropTypes.string,

  /** `panelWidth` is respected when the viewport is above this breakpoint */
  panelWidthBreakWidth: PropTypes.string,

  /** `preferredMode` is respected when the viewport is above this breakpoint */
  preferredModeBreakWidth: PropTypes.string,

  /** Text to be assigned to the panel's close button `aria-label` attribute */
  closePanelLabel: PropTypes.string
};
export default Panel;